import { RiPaintBrushLine } from "@itandi/itandi-bb-ui";
import { useState } from "react";
import { DealCardModal } from "~/components/common/deals/show/deal_card/DealCardModal";
import { EditMenuButton } from "~/components/common/parts/button/EditMenuButton";
import { useKtLeasings } from "~/hooks/useKtLeasings";
import { useKtLeasings as useVendorKtLeasings } from "~/hooks/vendors/useKtLeasings";
import type { Deal } from "~/models/Deal";
import {
	ESTIMATION_BY_LABEL,
	INSPECTION_BY_LABEL,
	matchDealType
} from "~/models/Deal";
import {
	getOriginDealVendor,
	isOriginVendor
} from "~/models/DealVendorCompany";
import { isActiveStatus } from "~/models/Status";
import type { VendorSession } from "~/models/VendorSession";
import {
	getFloorMapPath as getFloorMapClientPath,
	getInspectionFilePath,
	getLeaseContractFilePath as getLeaseContractFileClientPath
} from "~/utils/api/Deal";
import {
	getFloorMapPath as getFloorMapVendorPath,
	getLeaseContractFilePath as getLeaseContractFileVendorPath,
	getInspectionFilePath as getVendorInspectionFilePath
} from "~/utils/api/vendors/Deal";
import { formatDate } from "~/utils/DateUtil";
import { isEmpty } from "~/utils/StringUtil";
import Styles from "../DealCard.module.scss";

type Props = {
	deal: Deal;
	handleMutate?: () => Promise<void>;
	isVendor?: boolean;
	vendorSession?: VendorSession | null;
};

const notYetInput = <span className={Styles.NotYet}>未入力</span>;

const renderNameWithKana = (name: string | null, kana: string | null) => {
	if (isEmpty(name)) return notYetInput;
	return (
		<>
			<p>{name}</p>
			{!isEmpty(kana) && <p>（{kana}）</p>}
		</>
	);
};

const renderContactLink = (
	value: string | null,
	hrefPrefix: "tel:" | "mailto:"
) => {
	if (isEmpty(value)) return notYetInput;
	return (
		<a className={Styles.Link} href={hrefPrefix + value}>
			{value}
		</a>
	);
};

const renderDate = (value: string | null) => {
	if (isEmpty(value)) return notYetInput;
	return formatDate(value);
};

type InspectionFileRowProps = {
	deal: Deal;
	leasingId: string;
};

function VendorInspectionFileRow({ deal, leasingId }: InspectionFileRowProps) {
	const { data: leasing, isLoading } = useVendorKtLeasings(leasingId);
	return (
		<dl className={Styles.ListContent}>
			<dt className={Styles.ListContentCaption}>現況確認報告書</dt>
			<dd className={Styles.ListContentValue}>
				{isLoading ? null : leasing?.hasInspectionFile === true ? (
					<a
						className={Styles.Link}
						href={getVendorInspectionFilePath(deal.id)}
						rel="noreferrer"
						target="_blank"
					>
						ダウンロード
					</a>
				) : (
					notYetInput
				)}
			</dd>
		</dl>
	);
}

function ClientInspectionFileRow({ deal, leasingId }: InspectionFileRowProps) {
	const { data: leasing, isLoading } = useKtLeasings().leasingInfo(leasingId);
	return (
		<dl className={Styles.ListContent}>
			<dt className={Styles.ListContentCaption}>現況確認報告書</dt>
			<dd className={Styles.ListContentValue}>
				{isLoading ? null : leasing?.hasInspectionFile === true ? (
					<a
						className={Styles.Link}
						href={getInspectionFilePath(deal.id)}
						rel="noreferrer"
						target="_blank"
					>
						ダウンロード
					</a>
				) : (
					notYetInput
				)}
			</dd>
		</dl>
	);
}

// FIXME: commonと言いつつ管理か施工かで結構分岐してるので、componentを分けたい
export function DealCard({
	deal,
	vendorSession = null,
	isVendor = false,
	handleMutate
}: Props) {
	const [isModalOpen, setIsModalOpen] = useState(false);

	const handleClose = () => {
		setIsModalOpen(false);
	};

	const showTenant = getOriginDealVendor(deal)?.showTenant ?? false;
	const showInspectionFile =
		deal.leasingId !== null && (!isVendor || showTenant);

	return (
		<div className={Styles.DealCard}>
			<h3 className={Styles.Title}>
				<RiPaintBrushLine className={Styles.TitleIcon} />
				案件情報
				<div className={Styles.EditButtonWrapper}>
					{!isVendor && isActiveStatus(deal.statusId) && (
						<EditMenuButton
							onClick={() => {
								setIsModalOpen(true);
							}}
						>
							変更
						</EditMenuButton>
					)}
					{isModalOpen ? (
						<DealCardModal
							deal={deal}
							handleClose={handleClose}
							handleMutate={handleMutate}
						/>
					) : null}
				</div>
			</h3>
			<div className={Styles.Content}>
				{matchDealType(deal, "restoration") && (
					<>
						<dl className={Styles.ListContent}>
							<dt className={Styles.ListContentCaption}>立会担当会社</dt>
							<dd className={Styles.ListContentValue}>
								{INSPECTION_BY_LABEL[deal.inspectionBy]}
							</dd>
						</dl>
						<dl className={Styles.ListContent}>
							<dt className={Styles.ListContentCaption}>解約日</dt>
							<dd className={Styles.ListContentValue}>
								{renderDate(deal.terminationDate)}
							</dd>
						</dl>
					</>
				)}
				<dl className={Styles.ListContent}>
					<dt className={Styles.ListContentCaption}>原契約開始日</dt>
					<dd className={Styles.ListContentValue}>
						{renderDate(deal.leasingOriginStartDate)}
					</dd>
				</dl>
				<dl className={Styles.ListContent}>
					<dt className={Styles.ListContentCaption}>入居予定日</dt>
					<dd className={Styles.ListContentValue}>
						{renderDate(deal.preferredDate)}
					</dd>
				</dl>
				<dl className={Styles.ListContent}>
					<dt className={Styles.ListContentCaption}>契約者</dt>
					<dd className={Styles.ListContentValue}>
						{renderNameWithKana(deal.tenantName, deal.tenantNameKana)}
					</dd>
				</dl>
				<dl className={Styles.ListContent}>
					<dt className={Styles.ListContentCaption}>契約者電話番号</dt>
					<dd className={Styles.ListContentValue}>
						{renderContactLink(deal.tenantTel, "tel:")}
					</dd>
				</dl>
				<dl className={Styles.ListContent}>
					<dt className={Styles.ListContentCaption}>契約者メールアドレス</dt>
					<dd className={Styles.ListContentValue}>
						{renderContactLink(deal.tenantEmail, "mailto:")}
					</dd>
				</dl>
				<dl className={Styles.ListContent}>
					<dt className={Styles.ListContentCaption}>エレベーター</dt>
					<dd className={Styles.ListContentValue}>
						{deal.enableElevator ? "あり" : "なし"}
					</dd>
				</dl>
				<dl className={Styles.ListContent}>
					<dt className={Styles.ListContentCaption}>オートロック</dt>
					<dd className={Styles.ListContentValue}>
						{deal.enableAutoLock ? "あり" : "なし"}
					</dd>
				</dl>
				<dl className={Styles.ListContent}>
					<dt className={Styles.ListContentCaption}>鍵置き場</dt>
					<dd className={Styles.ListContentValue}>
						{isEmpty(deal.placeKeyStoring) ? (
							<>{notYetInput}</>
						) : (
							<>{deal.placeKeyStoring}</>
						)}
					</dd>
				</dl>
				<dl className={Styles.ListContent}>
					<dt className={Styles.ListContentCaption}>賃貸借契約書</dt>
					<dd className={Styles.ListContentValue}>
						{isEmpty(deal.leaseContractFile1) &&
						isEmpty(deal.leaseContractFile2) &&
						isEmpty(deal.leaseContractFile3) ? (
							<>{notYetInput}</>
						) : (
							<>
								{!isEmpty(deal.leaseContractFile1) && (
									<div>
										<a
											className={Styles.Link}
											href={
												vendorSession !== null
													? getLeaseContractFileVendorPath(deal.id, 1)
													: getLeaseContractFileClientPath(deal.id, 1)
											}
											rel="noreferrer"
											target="_blank"
										>
											賃貸借契約書1
											{deal.leaseContractFile1OriginalFilename !== null && (
												<>_{deal.leaseContractFile1OriginalFilename}</>
											)}
										</a>
									</div>
								)}
								{!isEmpty(deal.leaseContractFile2) && (
									<div>
										<a
											className={Styles.Link}
											href={
												vendorSession !== null
													? getLeaseContractFileVendorPath(deal.id, 2)
													: getLeaseContractFileClientPath(deal.id, 2)
											}
											rel="noreferrer"
											target="_blank"
										>
											賃貸借契約書2
											{deal.leaseContractFile2OriginalFilename !== null && (
												<>_{deal.leaseContractFile2OriginalFilename}</>
											)}
										</a>
									</div>
								)}
								{!isEmpty(deal.leaseContractFile3) && (
									<div>
										<a
											className={Styles.Link}
											href={
												vendorSession !== null
													? getLeaseContractFileVendorPath(deal.id, 3)
													: getLeaseContractFileClientPath(deal.id, 3)
											}
											rel="noreferrer"
											target="_blank"
										>
											賃貸借契約書3
											{deal.leaseContractFile3OriginalFilename !== null && (
												<>_{deal.leaseContractFile3OriginalFilename}</>
											)}
										</a>
									</div>
								)}
							</>
						)}
					</dd>
				</dl>
				<dl className={Styles.ListContent}>
					<dt className={Styles.ListContentCaption}>間取り図</dt>
					<dd className={Styles.ListContentValue}>
						{isEmpty(deal.floorMap) ? (
							<>{notYetInput}</>
						) : (
							<a
								className={Styles.Link}
								href={
									vendorSession !== null
										? getFloorMapVendorPath(deal.id)
										: getFloorMapClientPath(deal.id)
								}
								rel="noreferrer"
								target="_blank"
							>
								ダウンロード
							</a>
						)}
					</dd>
				</dl>
				{showInspectionFile && deal.leasingId !== null ? (
					isVendor ? (
						<VendorInspectionFileRow
							deal={deal}
							leasingId={String(deal.leasingId)}
						/>
					) : (
						<ClientInspectionFileRow
							deal={deal}
							leasingId={String(deal.leasingId)}
						/>
					)
				) : null}
				{(vendorSession === null ||
					isOriginVendor(deal, vendorSession.vendorCompany.id)) && (
					<dl className={Styles.ListContent}>
						<dt className={Styles.ListContentCaption}>管理会社担当者</dt>
						<dd className={Styles.ListContentValue}>
							{isEmpty(deal.staff.name) ? (
								<>{notYetInput}</>
							) : (
								<>
									<p>{deal.staff.name}</p>
									{!isEmpty(deal.staff.email) && (
										<p>
											<a
												className={Styles.Link}
												href={`mailto:${deal.staff.email}`}
											>
												{deal.staff.email}
											</a>
										</p>
									)}
								</>
							)}
						</dd>
					</dl>
				)}
				{(vendorSession === null ||
					isOriginVendor(deal, vendorSession.vendorCompany.id)) && (
					<dl className={Styles.ListContent}>
						<dt className={Styles.ListContentCaption}>発注書請書の宛名</dt>
						<dd className={Styles.ListContentValue}>
							{isEmpty(deal.estimateAddressName) ? (
								<>{notYetInput}</>
							) : (
								<>
									{!isEmpty(deal.estimateAddressName) && (
										<p>{deal.estimateAddressName}</p>
									)}
								</>
							)}
						</dd>
					</dl>
				)}

				<dl className={Styles.ListContent}>
					<dt className={Styles.ListContentCaption}>見積作成者</dt>
					<dd className={Styles.ListContentValue}>
						{ESTIMATION_BY_LABEL[deal.estimationBy]}
					</dd>
				</dl>

				<dl className={Styles.ListContent}>
					<dt className={Styles.ListContentCaption}>駐車場の場所</dt>
					<dd className={Styles.ListContentValue}>
						{isEmpty(deal.freeParking) ? (
							<>{notYetInput}</>
						) : (
							<p className={Styles.longSentence}>{deal.freeParking}</p>
						)}
					</dd>
				</dl>
				<dl className={Styles.ListContent}>
					<dt className={Styles.ListContentCaption}>その他要望</dt>
					<dd className={Styles.ListContentValue}>
						{isEmpty(deal.note) ? (
							<>{notYetInput}</>
						) : (
							<p className={Styles.longSentence}>{deal.note}</p>
						)}
					</dd>
				</dl>
			</div>
		</div>
	);
}
